import React, { useEffect, useRef, useState, useMemo } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { IonPage } from "@ionic/react";
import { Button, Typography, Row, Col, Form, Breadcrumb, Space, Spin, Input, notification, Result } from "antd";
import { Loading3QuartersOutlined } from "@ant-design/icons";

//
import log from "../../../utils/log";
import c from "../../../utils/common";
import moment from "moment-timezone";
import metaData from "../../../data/metaData";
import _ from "lodash";
import { showErrMsg } from "../../../utils/error";

//
import RequestMeta from "../../../components/RequestMeta";
import SectionHeader from "../../../components/GetStarted/SectionHeader";

//
import useAuthStore from "../../../states/useAuth";
import useRequest from "../../../hooks/useRequest";
import PageHeader from "../../../components/PageHeader";
import Status from "../../../components/Table/RequestStatus";
import useRequestStore from "../../../states/useRequest";
import RequestDetailEdit from "../../../components/ProductRequest/Edit";
import RequestDetailView from "../../../components/ProductRequest/View";
import deepcopy from "deepcopy";
import useShop from "../../../hooks/useShop";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const SingleProductRequest = () => {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams(); // Get the :id parameter from the URL
  const { resetRequest } = useRequestStore();
  const { user } = useAuthStore();
  const { getSingleProductRequest, updateProductRequest, createProduct } = useRequest();
  const { getAllianceShop } = useShop();

  //Sider
  const [collapsed, setCollapsed] = useState(false);

  const [productForm] = Form.useForm();

  const [originalRequest, setOriginalRequest] = useState();
  const [request, setRequest] = useState();
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [isFetchingSingleRequest, setIsFetchingSingleRequest] = useState(false);
  const [isFetchingAllianceShop, setIsFetchingAllianceShop] = useState(false);
  const [isShowErrorPage, setIsShowErrorPage] = useState(false);

  //
  const [allianceShops, setAllianceShops] = useState([]);

  const isActionBtnDisabled =
    id == "new" ? !c.hasDiff(originalRequest, request) : !originalRequest || !c.hasDiff(originalRequest, request);

  const updateRequestData = (newData) => {
    setRequest((prev) => {
      const updated = _.merge({}, prev, { info: newData });
      log.debug(prev);
      log.debug(newData);

      // Explicitly overwrite certain fields instead of merging
      if ("product" in newData && "discount" in newData.product) {
        updated.info.product.discount = newData.product.discount;
      }
      if ("product" in newData && "discountMethod" in newData.product) {
        updated.info.product.discountMethod = newData.product.discountMethod;
      }
      if ("product" in newData && "terms" in newData.product) {
        updated.info.product.terms = newData.product.terms;
      }
      if ("product" in newData && "gallery" in newData.product) {
        updated.info.product.gallery = newData.product.gallery;
      }
      if ("product" in newData && "redeemShopIds" in newData.product) {
        updated.info.product.redeemShopIds = newData.product.redeemShopIds;
      }

      log.debug(updated);
      return updated;
    });
  };

  // Function to fetch shop data
  const getAllianceShopData = async () => {
    let result;
    try {
      setIsFetchingAllianceShop(true);

      result = await getAllianceShop(user?.companyId, false, false);
      log.debug(result);

      //add key to disable antd table warning
      setAllianceShops(
        result.map((e, i) => {
          return { ...e, key: i };
        })
      );
    } catch (error) {
      notification.error({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Error
          </Title>
        ),
        description: <Text strong>Please refresh this page</Text>,
      });
    } finally {
      setIsFetchingAllianceShop(false);
    }
  };

  const getSingleRequestData = async () => {
    //setActiveTab
    let queryURL = location.search || undefined;
    log.debug(queryURL);
    // const queryParams = c.parseQueryToJSON(queryURL);
    // const { tab } = queryParams;
    // log.debug(tab);
    // setActiveTab(tab);

    //
    log.debug(id);
    if (id == "new") {
      return;
    }

    let result;

    try {
      setIsFetchingSingleRequest(true);
      result = await getSingleProductRequest(id);

      log.debug(result);
      setOriginalRequest(result);
      setRequest(result);
    } catch (error) {
      setIsShowErrorPage(true);
      notification.error({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Error
          </Title>
        ),
        description: <Text strong>Please refresh this page</Text>,
      });
    } finally {
      setIsFetchingSingleRequest(false);
    }
  };

  // Helper to validate the form
  const validateForm = async (productForm) => {
    try {
      await productForm.validateFields();
    } catch (error) {
      log.error(error);
      throw new Error("Some of the input fields are invalid. Please review and save again");
    }
  };

  // Helper to check if terms and gallery are valid
  const checkTermsAndGallery = (terms, gallery) => {
    if (!terms?.length) {
      throw new Error("At least one term required");
    }
    if (!gallery?.length) {
      throw new Error("Cover photo is required");
    }
  };

  // Helper to check time validity
  const checkTimeValidity = (time) => {
    if (time?.sale?.end && time?.redeem?.end) {
      const saleEnd = moment(time.sale.end);
      const redeemEnd = moment(time.redeem.end);

      if (saleEnd.isAfter(redeemEnd)) {
        throw new Error("The sale end date cannot be after redeem end date");
      }
    }
  };

  const checkRedeemShopIds = (redeemShopIds) => {
    if (!redeemShopIds?.length > 0) {
      throw new Error("At least 1 redeem shop");
    }
  };

  // Helper to remove populated data
  const removePopulatedData = (request, originalRequest) => {
    const cleanedOriginalRequest = deepcopy(originalRequest);
    const cleanedRequest = deepcopy(request);

    if (cleanedOriginalRequest?.info?.product) {
      delete cleanedOriginalRequest.info.product.redeemShops;
    }
    if (cleanedRequest?.info?.product) {
      delete cleanedRequest.info.product.redeemShops;
    }

    return { cleanedOriginalRequest, cleanedRequest };
  };

  // Helper to get the difference between requests
  const getRequestDiff = (cleanedOriginalRequest, cleanedRequest) => {
    const diff = c.getObjectDiff(cleanedOriginalRequest, cleanedRequest);
    if (!diff.info) {
      throw new Error("No changes found");
    }
    return diff;
  };

  // Main submit function
  const submitRequest = async () => {
    try {
      // Validate the form
      await validateForm(productForm);

      const { time, terms, gallery, redeemShopIds } = request?.info?.product || {};

      // Validate terms, gallery, and time
      checkTermsAndGallery(terms, gallery);
      checkTimeValidity(time);
      checkRedeemShopIds(redeemShopIds);

      // Clean the data and get the diff
      const { cleanedOriginalRequest, cleanedRequest } = removePopulatedData(request, originalRequest);
      const diff = getRequestDiff(cleanedOriginalRequest, cleanedRequest);

      log.debug("Changed fields:", diff);
      
      // Submit via axios
      setIsLoadingSubmit(true);
      let response;
      if (id === "new") {
        response = await createProduct({ product: diff.info.product });
      } else {
        response = await updateProductRequest(request.id, { info: diff.info, status: "pending" });
      }

      // Update state and notify success
      notification.success({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Success
          </Title>
        ),
      });

      // Redirect if new
      if (id === "new") {
        history.replace(`/merchantUser/product/${response.id}`);
      }
    } catch (error) {
      let message = error.response ? error.response.data : error.message;
      if (message.includes("Insufficient quota")) {
        notification.error({
          message: (
            <Title style={{ margin: 0 }} level={4}>
              Insufficient account quota
            </Title>
          ),
          description: <Text strong>Please contact us for more quota</Text>,
        });
      } else {
        showErrMsg(error);
      }
    } finally {
      setIsLoadingSubmit(false); // Ensure loading state is cleared
    }
  };

  const registerOnRefreshEvent = () => {
    const handleBeforeUnload = () => {
      resetRequest(); // Reset the state when the page is refreshed or closed
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload); // Properly remove the listener
    };
  };

  useEffect(() => {
    return registerOnRefreshEvent(); // Cleanup when component unmounts
  }, []);

  useEffect(() => {
    getSingleRequestData();
    getAllianceShopData();
  }, [id]);

  return (
    <div
      style={{
        margin: "0 auto",
        // background: "var(--ion-color-step-50)",
      }}
    >
      <Spin
        spinning={isFetchingSingleRequest}
        // percent="auto"
        indicator={<Loading3QuartersOutlined spin style={{ color: "white", fontSize: "2rem" }} />}
        fullscreen
      />

      {isShowErrorPage ? (
        <Result
          status="404"
          title="404"
          subTitle="Sorry, the page you visited does not exist."
          extra={
            <Button
              type="primary"
              onClick={() => {
                window.location.reload();
              }}
            >
              Refresh
            </Button>
          }
        />
      ) : (
        <>
          <Breadcrumb
            items={[
              {
                title: (
                  <a
                    onClick={() => {
                      history.push("/merchantUser/product");
                    }}
                  >
                    Product List
                  </a>
                ),
              },
              {
                title: <a>Product</a>,
              },
            ]}
          />

          <PageHeader
            title={
              <Space>
                {id != "new" ? "Request Detail" : "Create Product"}
                {id != "new" && request ? <Status status={request.status} /> : null}
              </Space>
            }
            isSticky
            actionGroup={
              request?.status == "approved" ? null : (
                <Space>
                  <Button
                    size="large"
                    disabled={isActionBtnDisabled}
                    loading={isLoadingSubmit}
                    color="default"
                    variant="filled"
                    style={{ marginRight: "1.5rem", width: "7.5rem" }}
                    onClick={async () => {
                      //reset
                      setRequest(originalRequest);
                    }}
                  >
                    Reset
                  </Button>

                  <Button
                    size="large"
                    type="primary"
                    disabled={isActionBtnDisabled}
                    loading={isLoadingSubmit}
                    style={{ marginRight: "1.5rem", width: "7.5rem" }}
                    onClick={async () => {
                      // let data = await parseFormData();
                      submitRequest();
                    }}
                  >
                    Submit
                  </Button>
                </Space>
              )
            }
            onBack={() => {
              history.goBack();
            }}
          />

          {isFetchingSingleRequest ? null : (
            <>
              {id != "new" && request ? <RequestMeta request={request} /> : null}

              <Row
                style={{
                  background: "var(--ion-color-step-50)",
                  paddingTop: "2rem",
                }}
              >
                {request?.status == "approved" ? (
                  <RequestDetailView
                    state={{
                      data: request,
                      form: productForm,
                      onChange: updateRequestData,
                    }}
                  />
                ) : (
                  <RequestDetailEdit
                    state={{
                      data: request?.info?.product,
                      form: productForm,
                      onChange: updateRequestData,
                    }}
                    allianceShops={allianceShops}
                  />
                )}
              </Row>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default SingleProductRequest;
